import type { MessageView } from '@sd/client-core';
import type { ConversationSummary, LinkPreview } from '@sd/contracts';
import { useEffect, useId, useState, type MouseEvent } from 'react';
import { errorMessage } from '../errors';
import { hostOf } from '../format';
import { Icon } from '../icons/Icon';
import { useConfirm, useToast } from '../ui/overlays';
import { useClient } from './context';

/** Object URL of a card's image, fetched from the company server (never the linked site) and released when the card goes. */
function useLinkThumbnail(preview: LinkPreview): string | null {
  const client = useClient();
  const [url, setUrl] = useState<string | null>(null);
  const previewId = preview.thumbnail ? preview.id : null;
  useEffect(() => {
    if (!previewId) {
      setUrl(null);
      return undefined;
    }
    let alive = true;
    client
      .linkThumbnailUrl(previewId)
      .then((u) => alive && setUrl(u))
      .catch(() => alive && setUrl(null));
    return () => {
      alive = false;
      client.releaseLinkThumbnail(previewId);
    };
  }, [client, previewId]);
  return url;
}

function Card({ preview, tabbable, onRemove }: { preview: LinkPreview; tabbable: boolean; onRemove: ((event: MouseEvent<HTMLButtonElement>) => void) | null }) {
  const thumbnail = useLinkThumbnail(preview);
  // An image that does not load leaves the card as it is without it, not a broken picture.
  const [broken, setBroken] = useState(false);
  const descriptionId = useId();
  const site = preview.siteName ?? hostOf(preview.url);
  const tabIndex = tabbable ? undefined : -1;
  return (
    <div className="lpv">
      {/* Opened like the link in the text: a new browser tab on the web, the system browser on desktop. */}
      <a
        className="lpv-link"
        href={preview.url}
        target="_blank"
        rel="noopener noreferrer"
        tabIndex={tabIndex}
        aria-label={`링크 미리보기: ${preview.title}, ${site}`}
        aria-describedby={preview.description ? descriptionId : undefined}
      >
        <span className="lpv-text">
          <span className="lpv-site">{site}</span>
          <span className="lpv-title">{preview.title}</span>
          {preview.description ? (
            <span className="lpv-desc" id={descriptionId}>
              {preview.description}
            </span>
          ) : null}
        </span>
        {preview.thumbnail && !broken ? <span className="lpv-thumb">{thumbnail ? <img src={thumbnail} alt="" onError={() => setBroken(true)} /> : null}</span> : null}
      </a>
      {onRemove ? (
        <button type="button" className="lpv-x" tabIndex={tabIndex} onClick={onRemove} aria-label="미리보기 제거" data-tip="미리보기 제거">
          <Icon name="x" size="sm" />
        </button>
      ) : null}
    </div>
  );
}

/**
 * Link preview cards under a message (R47). Only fetched pages appear: a link that has no
 * preview (an intranet page, a site that did not answer) shows nothing, as before.
 */
export function LinkPreviewCards({ view, conversation, tabbable }: { view: MessageView; conversation: ConversationSummary; tabbable: boolean }) {
  const client = useClient();
  const confirm = useConfirm();
  const toast = useToast();
  const canRemove = view.mine && Boolean(view.id) && !conversation.readOnly;

  const remove = async (preview: LinkPreview, event: MouseEvent<HTMLButtonElement>) => {
    const row = event.currentTarget.closest<HTMLElement>('.row');
    const ok = await confirm({ title: '미리보기를 제거할까요?', body: '이 대화의 모든 사람에게서 미리보기가 사라져요. 메시지의 링크는 그대로 남아요.', ok: '제거', danger: true });
    if (!ok || !view.id) return;
    try {
      await client.removeLinkPreview(conversation.id, view.id, preview.id);
      toast('미리보기를 제거했어요');
      // The button that held focus is gone: keep the keyboard on the message.
      if (document.activeElement === document.body) row?.focus();
    } catch (error) {
      toast(errorMessage(error));
    }
  };

  // Only web addresses become links, whatever a server sends.
  const previews = view.linkPreviews.filter((preview) => /^https?:\/\//i.test(preview.url));
  if (!previews.length) return null;
  return (
    <div className="lpvs">
      {previews.map((preview) => (
        <Card key={preview.id} preview={preview} tabbable={tabbable} onRemove={canRemove ? (event) => void remove(preview, event) : null} />
      ))}
    </div>
  );
}
